import { Connector } from '@web3-react/types';
import { ConnectorType, getConnector } from './index';
import { ConnectorMetadata } from './types';
// Logo imports - using existing assets temporarily
const metamaskLogo = '/static/metamask.svg';
const walletConnectLogo = '/static/portis.svg'; // Temporarily reuse existing logo

/**
 * Connector activation utility
 * Provides a clean interface for activating specific connectors
 */
export const activateConnector = async (
  connectorType: ConnectorType
): Promise<void> => {
  try {
    const connector = getConnector(connectorType);
    if (!connector) {
      throw new Error(`Connector ${connectorType} is not available`);
    }
    await connector.activate();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to activate ${connectorType}:`, error);
    throw error;
  }
};

/**
 * Connector deactivation utility
 */
export const deactivateConnector = (connectorType: ConnectorType): void => {
  try {
    const connector = getConnector(connectorType);
    if (connector && connector.deactivate) {
      connector.deactivate();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to deactivate ${connectorType}:`, error);
  }
};

/**
 * Check if a connector is currently active
 * Note: This function cannot use hooks and should be called from within a React component
 */
export const getConnectorActiveStatus = (
  connectorType: ConnectorType
): boolean => {
  // This is a placeholder - actual usage requires hooks within React components
  return false;
};

/**
 * Get connector metadata for UI display
 */
export const getConnectorMetadata = (
  connectorType: ConnectorType
): ConnectorMetadata => {
  switch (connectorType) {
    case ConnectorType.METAMASK:
      return {
        name: 'MetaMask',
        logo: metamaskLogo,
        description: 'Connect using your MetaMask wallet',
        href: 'https://metamask.io/',
      };
    case ConnectorType.WALLETCONNECT:
      return {
        name: 'WalletConnect',
        logo: walletConnectLogo,
        description: 'Connect using WalletConnect v2 protocol',
        href: 'https://walletconnect.com/',
      };
    default:
      throw new Error(`Unknown connector type: ${connectorType}`);
  }
};

/**
 * Get all available connectors with metadata
 */
export const getAllConnectors = (): Array<{
  type: ConnectorType;
  metadata: ConnectorMetadata;
  connector: Connector | null;
}> => {
  return Object.values(ConnectorType).map(type => ({
    type,
    metadata: getConnectorMetadata(type),
    connector: getConnector(type),
  }));
};

/**
 * Connection priority helper
 * Determines which connector to try first based on user agent, previous connections, etc.
 */
export const getPreferredConnector = (): ConnectorType => {
  // Check if MetaMask is available
  if (typeof window !== 'undefined' && window.ethereum?.isMetaMask) {
    return ConnectorType.METAMASK;
  }

  // Check for mobile user agents that might prefer WalletConnect
  if (
    typeof window !== 'undefined' &&
    /Mobi|Android/i.test(navigator.userAgent)
  ) {
    return ConnectorType.WALLETCONNECT;
  }

  // Default to MetaMask
  return ConnectorType.METAMASK;
};

/**
 * Error helpers for connector-specific error handling
 */
export const isUserRejectedError = (error: Error): boolean => {
  return (
    error.message.toLowerCase().includes('user rejected') ||
    error.message.toLowerCase().includes('user denied') ||
    error.message.toLowerCase().includes('user cancelled')
  );
};

export const isNoProviderError = (error: Error): boolean => {
  return (
    error.message.toLowerCase().includes('no ethereum provider') ||
    error.message.toLowerCase().includes('no provider found')
  );
};

export const isUnsupportedChainError = (error: Error): boolean => {
  return (
    error.message.toLowerCase().includes('unsupported chain') ||
    error.message.toLowerCase().includes('chain not added')
  );
};

/**
 * Network switching helper
 * Attempts to switch networks using the active connector
 */
export const switchNetwork = async (
  connectorType: ConnectorType,
  chainId: number
): Promise<void> => {
  try {
    // MetaMask supports network switching
    if (connectorType === ConnectorType.METAMASK && window.ethereum) {
      await (window.ethereum as any).request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
    } else {
      throw new Error('Network switching not supported for this connector');
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to switch network for ${connectorType}:`, error);
    throw error;
  }
};

/**
 * Eager connection helper
 * Attempts to connect to previously connected wallets on app load
 */
export const attemptEagerConnection = async (): Promise<ConnectorType | null> => {
  // Try MetaMask first if available
  try {
    const metaMaskConnector = getConnector(ConnectorType.METAMASK);
    if (metaMaskConnector && metaMaskConnector.connectEagerly) {
      await metaMaskConnector.connectEagerly();
      return ConnectorType.METAMASK;
    }
  } catch (error) {
    // Silent fail for eager connection
  }

  // Try WalletConnect
  try {
    const walletConnectConnector = getConnector(ConnectorType.WALLETCONNECT);
    if (walletConnectConnector && walletConnectConnector.connectEagerly) {
      await walletConnectConnector.connectEagerly();
      return ConnectorType.WALLETCONNECT;
    }
  } catch (error) {
    // Silent fail for eager connection
  }

  return null;
};
